import { useParams, Link, useNavigate } from "react-router-dom";
import { CAMERA_EDITORIAL_NOTES } from "@/data/cameraEditorialNotes";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import { MarketingHeader } from "@/components/MarketingHeader";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Helmet as Head } from 'react-helmet-async';
import { getVisibleSpec } from '@/data/visibleSpecsIndex';
import FovVisualization from './FovVisualization';
import '@/pages/landing-animations.css';
import {
  getCameraBySlug,
  getCameraByPathSlugs,
  getRelatedCameras,
  getCrossSellCameras,
  formatResolution,
  getMegapixels,
  getCameraTypeName,
  getEffectiveRangeM,
  getDORIDistanceM,
} from "@/lib/cameraSlug";
import { getBrandHubPath } from "@/lib/brandSlug";
import { getComparisonsForCamera } from "@/lib/skuComparisonLookup";
import {
  generateIntroParagraph,
  generateUseCases,
  generateStrengths,
  generateConsiderations,
  generateInstallationTips,
  generateCoverageMath,
  generatePowerCabling,
  generatePrivacyConsiderations,
  generateExtendedFAQ,
  generateInstallerCostNote,
} from "@/lib/cameraNarrative";
import {
  updateMetaTags,
  SchemaScript,
  generateBreadcrumbSchema,
  generateFAQSchema,
} from "@/lib/seoHelpers";
import {
  ArrowRight,
  Camera,
  CheckCircle,
  Eye,
  Zap,
  Shield,
  ChevronRight,
  Sun,
  Moon,
  Maximize2,
  Crosshair,
  Radio,
  ExternalLink,
  PencilRuler,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import { getManufacturerVerified } from "@/lib/manufacturerVerified";
import { cameraSeoHead, frozenSeoHead, makerSourceUrl } from "@/lib/cameraSeoHead";
import { CAMERA_SEO_FROZEN } from "@/data/camera-seo-frozen.generated";

const calculateCoverage = (fovDeg: number, distance: number): number =>
  2 * distance * Math.tan((fovDeg * Math.PI) / 360);

const CameraPage = () => {
  const { brandSlug, modelSlug } = useParams<{ brandSlug: string; modelSlug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  // Narrative content (intro, use cases, strengths, etc.) is generated in
  // English only by cameraNarrative.ts. Showing it under PL/DE/ES UI mixed
  // with translated chrome looks half-baked, so we gate the EN-only sections
  // behind this flag. SEO impact is minimal — non-EN camera SKU lookups have
  // very low impressions per GSC. Until we translate the narrative templates,
  // non-EN visitors get a clean specs-and-numbers page.
  const isEnglish = currentLanguage === 'en';

  const camera = brandSlug && modelSlug ? getCameraByPathSlugs(brandSlug, modelSlug) : undefined;
  // Legacy flat slug: still the key for editorial notes, comparisons and the
  // designer deep link (?camera=<slug>).
  const slug = camera?.slug;
  const pageUrl = camera ? `https://cctvplanner.io${camera.path}` : '';
  const related = camera ? getRelatedCameras(camera) : [];
  const crossSell = camera ? getCrossSellCameras(camera) : [];
  // Comparison pages that feature this camera. These pages are sitemapped but
  // had no inbound link anywhere on the site; this rail is their crawl path.
  const comparisons = camera ? getComparisonsForCamera(camera.slug) : [];
  // Null for brands below the hub threshold — the breadcrumb then stays plain
  // text instead of pointing at a page that does not exist.
  const brandHubPath = camera ? getBrandHubPath(camera.manufacturer) : null;

  const mp = camera ? getMegapixels(camera.resolution) : 0;
  // Effective range derived from DORI recognize threshold (125 px/m) instead
  // of the placeholder `camera.range` field, which mirrors irRange on ~48% of
  // the catalog. See camera-audit-2026-05-20.md for the audit context.
  const effRange = camera ? getEffectiveRangeM(camera) : 0;
  // Real datasheet low-light figure (verified models only) — replaces the old
  // hardcoded "0.001 lux" claim. Undefined for unaudited models → generic copy.
  const visEntry = camera ? getVisibleSpec(camera.model) : undefined;
  const isVerified = visEntry?.status === 'verified';
  const vspec = isVerified ? visEntry!.spec : undefined;
  const lowLightLux = isVerified
    ? (vspec!.minIlluminationColorLux ?? vspec!.minIllumination_lux)
    : undefined;
  // First datasheet-grade source for the verified spec (prefer a PDF / a URL
  // that mentions "datasheet"; else fall back to the first listed source).
  // Mirrors NVRPage's `datasheetUrl` pattern, but the camera audit stores
  // sources as a string[] rather than a single field.
  const datasheetUrl = isVerified
    ? (visEntry!.sources.find(s => /\.pdf(\?|$)/i.test(s) || /datasheet/i.test(s)) ?? visEntry!.sources[0])
    : undefined;
  // the maker's own page every catalogue row with the maker's figures was read from
  const makerUrl = camera ? makerSourceUrl(camera) : undefined;
  const sourceUrl = datasheetUrl ?? makerUrl;
  // Brand-attested state from the /verify manufacturer panels. Entries
  // exist only for verdicts the founder applied AND whose attested values
  // still pin to the current catalog (see the generated file's header),
  // so rendering the badge needs no further checks. Deterministic date
  // formatting keeps SSG output byte-identical between builds.
  const mfgVerified = camera ? getManufacturerVerified(camera.manufacturer, camera.model) : null;
  const mfgVerifiedDate = mfgVerified
    ? new Date(`${mfgVerified.verifiedAt}T00:00:00Z`).toLocaleDateString(currentLanguage === 'en' ? 'en-GB' : `${currentLanguage}-u-ca-gregory`, { month: 'long', year: 'numeric', timeZone: 'UTC' })
    : undefined;

  // Single source of truth for title/description. Used by both <Helmet>
  // (for the initial HTML head) and updateMetaTags (for SPA navigations).
  // They MUST be identical — if they drift, Google sees inconsistent meta
  // on re-crawl and demotes. A page Google shows whose catalogue figures
  // changed keeps the head it ranks with, word for word (CAMERA_SEO_FROZEN).
  const frozenHead = camera ? CAMERA_SEO_FROZEN[camera.path] : undefined;
  const seoHead = camera ? (frozenHead ? frozenSeoHead(frozenHead, camera.manufacturer) : cameraSeoHead(camera)) : null;
  const seoTitle = seoHead ? seoHead.title : "Camera not found | CCTVplanner";
  const seoDescription = seoHead ? seoHead.description : "";

  useEffect(() => {
    // `!slug` is implied by `!camera`; stated so the canonical below is a
    // string rather than a possible ".../camera/undefined".
    if (!camera || !slug) return;

    updateMetaTags({
      title: seoTitle,
      description: seoDescription,
      keywords: `${camera.manufacturer} ${camera.model}, ${camera.model} specs, ${camera.model} FOV, ${camera.model} datasheet, ${camera.model} dimensions, ${getCameraTypeName(camera.type)}, CCTV camera, ${camera.manufacturer} cameras`,
      ogTitle: seoHead!.ogTitle,
      ogDescription: seoDescription,
      canonicalUrl: pageUrl,
      ogType: "product",
    });

    window.scrollTo(0, 0);
  }, [camera, pageUrl, seoTitle, seoDescription]);

  // `!slug` is already implied by `!camera` (the lookup above only runs with a
  // slug), but stating it lets the compiler narrow `slug` to a string for the
  // rest of the component. Every canonical, hreflang, og:url and JSON-LD URL
  // below interpolates it, and none of them may ever read ".../camera/undefined".
  if (!slug || !camera) {
    // Soft-404 mitigation. The route is in our sitemap but the slug
    // doesn't match any known camera (typo, retired model, stale link).
    // Without `noindex` Google sees a 200 page with one-line content
    // and flags it as Soft 404 in Search Console (10 such pages reported).
    // `prerender-status-code` is honoured by Netlify, Vercel and most
    // headless prerenderers; on lh.pl FTP it's a no-op but doesn't hurt.
    //
    // Canonical to /cameras (live catalog index) instead of letting Apache
    // SPA-fallback's index.html canonical-to-root take over. Combined with
    // the index.html canonical removal (May 2026), stops Google from flagging
    // stale-slug pages as "Alternate page with proper canonical tag" pointing
    // at the homepage (342 URLs affected in last GSC validation).
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Head>
          <title>{t('cam_not_found_title', { defaultValue: 'Camera not found — CCTVplanner' })}</title>
          <meta name="robots" content="noindex,follow" />
          <meta name="prerender-status-code" content="404" />
          <link rel="canonical" href="https://cctvplanner.io/cameras" />
        </Head>
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">{t('cam_not_found', { defaultValue: 'Camera not found' })}</h1>
          <Link to="/" className="text-[var(--color-brand-blue-text)] hover:underline">
            {t('cam_go_back_home', { defaultValue: 'Go back to home' })}
          </Link>
        </div>
      </div>
    );
  }

  const handleCTA = () => {
    // Deep link: the designer reads ?camera=<slug> and drops THIS model at
    // the viewport centre with coverage zones on. /cctv-design sits behind
    // ProtectedRoute, so an anonymous visitor hits the auth wall FIRST —
    // with the full path (incl. ?camera) preserved in the redirect param —
    // and gets the placed camera right after signing in. No sandbox, no
    // limit bypass: placement runs through the normal addCamera gates and
    // saves are server-enforced regardless.
    navigate(`/cctv-design?camera=${slug}`);
  };

  // Generate unique narrative content per camera (avoids thin-content SEO penalty)
  const introParagraph = generateIntroParagraph(camera);
  const useCases = generateUseCases(camera);
  const strengths = generateStrengths(camera);
  const considerations = generateConsiderations(camera);
  const installationTips = generateInstallationTips(camera);
  const coverageMath = generateCoverageMath(camera);
  const powerCabling = generatePowerCabling(camera);
  const privacyNote = generatePrivacyConsiderations(camera);
  const extendedFaq = generateExtendedFAQ(camera);
  const installerCostNote = generateInstallerCostNote(camera);

  const distances = [5, 10, 15, 20, 30, 50];
  const typeIcon =
    camera.type === "dome" ? Shield :
    camera.type === "ptz" ? Radio :
    camera.type === "panoramic" ? Maximize2 :
    Crosshair;
  const TypeIcon = typeIcon;

  return (
    // Graphite atmosphere instead of flat black: deep blue-graphite ground
    // with the design system's subtle brand haze (same class the premium
    // landing panels use), so the page reads designed rather than unlit.
    <div className="min-h-screen bg-background text-foreground">
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        {/* Static og:title / og:description so the PRERENDERED HTML carries
            per-camera Open Graph. Previously these were only set post-hydration
            via updateMetaTags, so the ~44k prerendered pages shipped the index
            defaults — social/crawler previews saw the generic homepage card.
            Mirrors seoTitle/seoDescription exactly to avoid signal drift. */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        {/* Self-canonical. Camera detail pages are intentionally excluded
            from the global HreflangTags injector (they're EN-only with no
            translated counterparts), so canonical has to live here.
            Without it Google sees /camera/[slug] as "Duplicate without
            user-selected canonical" and may pick a different URL as
            primary (the dominant cause of the 32 + 143 GSC duplicate flags). */}
        <link rel="canonical" href={pageUrl} />
        {/* Self-ref hreflang: declares EN canonical + x-default fallback to
            the same EN URL. Camera pages are EN-only — without these tags
            Google may show non-English SERPs no result, or treat the EN URL
            as duplicate when a localized user searches for the same model. */}
        <link rel="alternate" hrefLang="en" href={pageUrl} />
        <link rel="alternate" hrefLang="x-default" href={pageUrl} />
        <meta property="og:url" content={pageUrl} />
        {/* Per-page og:image override — until per-camera Satori OG ships,
            falls back to the sized homepage card so social cards render
            consistently. The TechArticle/Product schema below also uses
            this URL so all three signals are consistent. */}
        <meta property="og:image" content="https://cctvplanner.io/og/home.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${camera.manufacturer} ${camera.model} — datasheet, FOV cones, coverage planner`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://cctvplanner.io/og/home.png" />
      </Head>

        <SchemaScript schema={generateBreadcrumbSchema([
          { name: "Home", url: "/" },
          // `url` stays undefined for brands with no hub, so the helper emits a
          // nameless-URL ListItem rather than a link to a page that 404s.
          { name: camera.manufacturer, url: brandHubPath ?? undefined },
          { name: camera.model, url: camera.path },
        ])} />

        {/*
          TechArticle schema (NOT Product). The page is informational —
          we don't sell or stock this camera, so a Product schema without
          `offers` / `aggregateRating` / `review` is invalid per Google's
          Product rich-result rules and gets suppressed (1318 pages → 18
          impressions before this fix). TechArticle is the right type for
          "educational write-up about a product" and has no offer / rating
          requirements. The Product remains as a nested `about` reference
          so the technical metadata still feeds Google's knowledge graph
          without making false sales claims.

          Pattern matches CameraModelPage.tsx which already used TechArticle.
        */}
        <SchemaScript schema={{
          "@context": "https://schema.org",
          "@type": "TechArticle",
          headline: `${camera.manufacturer} ${camera.model} — Specs, FOV & Coverage`,
          description: seoDescription,
          // og-image.png was 404 on prod (file never existed); use the real
          // sized OG card. Per-camera Satori OG is a planned Phase D upgrade.
          image: `https://cctvplanner.io/og/home.png`,
          url: pageUrl,
          mainEntityOfPage: pageUrl,
          inLanguage: "en",
          isAccessibleForFree: true,
          datePublished: "2024-01-01",
          // No dateModified: the build clock is not the day this camera's specs
          // changed, and stamping today on every page at every build published a
          // change that never happened. Restore it only from a real per-camera
          // date, alongside the sitemap lastmod in scripts/generate-sitemap.mjs.
          author: {
            "@type": "Organization",
            name: "CCTVplanner",
            url: "https://cctvplanner.io",
          },
          publisher: {
            "@type": "Organization",
            name: "CCTVplanner",
            url: "https://cctvplanner.io",
            logo: {
              "@type": "ImageObject",
              url: "https://cctvplanner.io/og/home.png",
            },
          },
          about: {
            "@type": "Product",
            name: `${camera.manufacturer} ${camera.model}`,
            // Product.image is required by Google for any Product schema
            // to qualify for Knowledge Graph / image-SERP / shopping
            // carousels. Until Satori per-camera OG ships in Phase D, we
            // use the sized homepage OG as a sensible fallback (better
            // than no image at all — Google was suppressing the Product
            // graph entirely without it).
            image: "https://cctvplanner.io/og/home.png",
            brand: { "@type": "Brand", name: camera.manufacturer },
            manufacturer: { "@type": "Organization", name: camera.manufacturer },
            category: getCameraTypeName(camera.type),
            sku: camera.model,
            mpn: camera.model,
            additionalProperty: [
              camera.resolution && {
                "@type": "PropertyValue",
                name: "Resolution",
                value: formatResolution(camera.resolution),
              },
              {
                "@type": "PropertyValue",
                name: "Horizontal Field of View",
                value: `${camera.hfov}°`,
              },
              effRange > 0 && {
                "@type": "PropertyValue",
                name: "Effective Range",
                value: `${effRange} m`,
              },
              camera.irRange > 0 && {
                "@type": "PropertyValue",
                name: "IR Range",
                value: `${camera.irRange} m`,
              },
              camera.focalLength && {
                "@type": "PropertyValue",
                name: "Focal Length",
                value: camera.focalLength,
              },
              {
                "@type": "PropertyValue",
                name: "Camera Type",
                value: getCameraTypeName(camera.type),
              },
              camera.superLowLight && {
                "@type": "PropertyValue",
                name: "Low Light Performance",
                value: typeof lowLightLux === 'number'
                  ? `Super Low Light / Starlight (${lowLightLux} lux)`
                  : "Super Low Light / Starlight",
              },
              // Datasheet-verified properties (only when an audited spec exists)
              // — feeds the same numbers the user-visible table shows into the
              // Product knowledge graph so model/datasheet queries get matched.
              ...(vspec ? [
                vspec.sensorSize && { "@type": "PropertyValue", name: "Sensor Size", value: vspec.sensorSize },
                vspec.ipRating && { "@type": "PropertyValue", name: "Ingress Protection", value: vspec.ipRating },
                vspec.ikRating && { "@type": "PropertyValue", name: "Impact Rating", value: vspec.ikRating },
                typeof vspec.powerConsumption_w === 'number' && { "@type": "PropertyValue", name: "Power Consumption", value: `${vspec.powerConsumption_w} W` },
                typeof vspec.maxBitrate_mbps === 'number' && { "@type": "PropertyValue", name: "Max Bitrate", value: `${vspec.maxBitrate_mbps} Mbps` },
                vspec.codecsSupported?.length && { "@type": "PropertyValue", name: "Codecs", value: vspec.codecsSupported.join(', ') },
                vspec.onvifProfiles?.length && { "@type": "PropertyValue", name: "ONVIF Profiles", value: vspec.onvifProfiles.map((p) => `Profile ${p}`).join(', ') },
                (typeof vspec.operatingTemp_min_c === 'number' && typeof vspec.operatingTemp_max_c === 'number') && { "@type": "PropertyValue", name: "Operating Temperature", value: `${vspec.operatingTemp_min_c}°C to ${vspec.operatingTemp_max_c}°C` },
                typeof vspec.ndaaCompliant === 'boolean' && { "@type": "PropertyValue", name: "NDAA 889 Compliant", value: vspec.ndaaCompliant ? "Yes" : "No" },
              ] : []),
            ].filter(Boolean),
          },
        }} />

        <SchemaScript schema={generateFAQSchema([
          {
            question: `What is the field of view of ${camera.manufacturer} ${camera.model}?`,
            answer: `The ${camera.manufacturer} ${camera.model} has a horizontal field of view of ${camera.hfov}° with a ${camera.focalLength || "standard"} lens. At 10 meters distance, it covers approximately ${calculateCoverage(camera.hfov, 10).toFixed(1)} meters width.`,
          },
          {
            question: `What is the IR range of ${camera.manufacturer} ${camera.model}?`,
            answer: `The ${camera.manufacturer} ${camera.model} features ${camera.irRange}m infrared illumination range for night vision, making it suitable for ${camera.irRange >= 50 ? "long-range outdoor" : camera.irRange >= 30 ? "medium-range" : "short-range indoor"} surveillance.`,
          },
          // no resolution: no pixel density to read a distance from, so no answer to give
          ...(effRange > 0 ? [{
            question: `How far can ${camera.manufacturer} ${camera.model} see clearly?`,
            answer: `The recommended effective range of ${camera.manufacturer} ${camera.model} is approximately ${effRange} meters (DORI Recognize threshold, ≥125 px/m on the target). At this distance the ${formatResolution(camera.resolution)} sensor provides enough pixel density to describe a person's actions and clothing; identify-grade detail requires roughly half this distance.`,
          }] : []),
          // Extended FAQ — adds 5 more concrete Q&A entries computed from
          // the camera's actual specs. Feeds Google's FAQPage rich result
          // and adds 200-300 unique words per page (the main "thin content"
          // mitigation in this round of SEO fixes).
          ...extendedFaq,
        ])} />

      <MarketingHeader />

      <main id="main-content">
        {/* HERO */}
        <section
          // Faint concentric survey arcs behind the hero — the design
          // system's surveying-instrument motif, on-theme for a coverage
          // planner and enough texture that the ground stops reading as
          // empty black.
          className="border-b bg-optical-arcs"
          style={{
            paddingTop: "calc(var(--header-height) + 4rem)",
            paddingBottom: "4rem",
            borderColor: "var(--color-gray-border)",
          }}
        >
          <div className="pelco-container">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-[12px] mb-6" style={{ fontFamily: "var(--font-mono), monospace", color: "var(--color-gray-muted)" }}>
              <Link to="/" className="hover:text-foreground transition-colors">{t('cam_breadcrumb_home', { defaultValue: 'Home' })}</Link>
              <ChevronRight className="w-3 h-3" />
              {brandHubPath
                ? <Link to={brandHubPath} className="hover:text-foreground transition-colors">{camera.manufacturer}</Link>
                : <span>{camera.manufacturer}</span>}
              <ChevronRight className="w-3 h-3" />
              <span style={{ color: "var(--color-gray-dark)" }}>{camera.model}</span>
            </nav>

            {/* Eyebrow carries the page's brand accent — the rest of the hero
                stays monochrome so the single blue line reads as deliberate. */}
            <p className="bd-eyebrow" style={{ color: "var(--color-brand-blue-text)" }}>
              CCTVPLANNER.IO · CAMERA · {camera.manufacturer.toUpperCase()}
            </p>
            <h1 className="bd-h1 max-w-3xl">
              {seoHead!.h1}
            </h1>
            <p className="bd-lead max-w-3xl">
              {t('cam_hero_description', {
                defaultValue: '{{type}} with {{resolution}} resolution, {{hfov}}° field of view, and {{irRange}}m IR night vision range.',
                type: getCameraTypeName(camera.type),
                resolution: formatResolution(camera.resolution),
                hfov: camera.hfov,
                irRange: camera.irRange,
              })}
              {camera.superLowLight && ` ${t('cam_hero_super_low_light', { defaultValue: 'Features Super Low Light technology for superior night performance.' })}`}
            </p>
            {/* Unique, datasheet-grounded editorial intro (EN-only, keyed by slug).
                Breaks the template near-duplicate-prose ceiling on pages that have
                one — currently the 2026-07 additions; grows per catalog round. */}
            {slug && CAMERA_EDITORIAL_NOTES[slug] && (
              <p className="bd-lead max-w-3xl mt-4">{CAMERA_EDITORIAL_NOTES[slug]}</p>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={handleCTA} className="bd-btn bd-btn-primary">
                {t('cam_plan_layout_cta', { defaultValue: 'Plan layout with this camera' })}
                <ArrowRight className="w-4 h-4" />
              </button>
              {/* Frictionless path alongside the deep link: /cctv-design sits
                  behind the auth wall, and /demo converts at ~36% — the SEO
                  pages must keep a no-account entry or anonymous visitors
                  bounce off the login screen. */}
              <Link
                to="/demo"
                className="text-sm font-medium text-[var(--color-gray-muted)] hover:text-[var(--color-brand-blue-text)] hover:underline"
              >
                {t('cam_demo_link', { defaultValue: 'or explore the live demo first, no account needed' })}
              </Link>
            </div>
          </div>
        </section>

        {/* STATS STRIP */}
        <section
          className="border-b"
          style={{ borderColor: "var(--color-gray-border)", backgroundColor: "var(--color-surface)" }}
        >
          <div className="pelco-container py-10">
            <div
              className="grid grid-cols-2 md:grid-cols-4 border"
              style={{ borderColor: "var(--color-gray-border)" }}
            >
              {[
                { value: mp > 0 ? `${mp}MP` : formatResolution(camera.resolution), label: t('cam_spec_resolution', { defaultValue: 'Resolution' }), accent: "var(--color-brand-blue)" },
                { value: `${camera.hfov}°`, label: t('cam_spec_horizontal_fov', { defaultValue: 'Horizontal FOV' }), accent: "var(--color-brand-blue)" },
                { value: effRange > 0 ? `${effRange}m` : t('cam_spec_na', { defaultValue: 'N/A' }), label: t('cam_spec_effective_range', { defaultValue: 'Effective Range' }), accent: "var(--color-brand-blue)" },
                // IR is red EVERYWHERE on this page (stat card, FOV plot arc,
                // legend) — one colour, one meaning.
                { value: `${camera.irRange}m`, label: t('cam_spec_ir_range', { defaultValue: 'IR Range' }), accent: "#f87171" },
              ].map((s, i) => (
                <div
                  key={s.label}
                  className="p-6"
                  style={{
                    borderLeft: i % 2 !== 0 ? "1px solid var(--color-gray-border)" : undefined,
                    borderTop: i >= 2 ? "1px solid var(--color-gray-border)" : undefined,
                    boxShadow: `inset 0 2px 0 0 ${s.accent}`,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-mono), monospace",
                      fontSize: "10px",
                      letterSpacing: "0.1em",
                      // Brand blue as text uses the contrast-safe tint.
                      color: s.accent === "var(--color-brand-blue)" ? "var(--color-brand-blue-text)" : s.accent,
                    }}
                  >
                    NO.0{i + 1}
                  </div>
                  <div
                    className="mt-2"
                    style={{
                      fontFamily: "var(--font-display), 'Fraunces', serif",
                      fontSize: "clamp(28px, 3vw, 36px)",
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      color: "var(--color-gray-dark)",
                    }}
                  >
                    {s.value}
                  </div>
                  <div className="bd-body mt-1" style={{ fontSize: "12px" }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="pelco-container py-12">

          {/* FULL SPECS TABLE */}
          <section className="mb-16">
            <p className="bd-eyebrow" style={{ color: "var(--color-brand-blue-text)" }}>SPECIFICATIONS · {camera.model.toUpperCase()}</p>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <h2 className="bd-h2 m-0">{t('cam_specifications', { defaultValue: 'Full specifications' })}</h2>
              {vspec && (
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full"
                  style={{
                    color: "rgb(16,185,129)",
                    backgroundColor: "rgba(16,185,129,0.12)",
                    border: "1px solid rgba(16,185,129,0.35)",
                  }}
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  {t('cam_spec_verified_badge', { defaultValue: 'Verified from manufacturer datasheet' })}
                </span>
              )}
              {mfgVerified && (
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full"
                  style={{
                    color: "rgb(59,130,246)",
                    backgroundColor: "rgba(59,130,246,0.12)",
                    border: "1px solid rgba(59,130,246,0.35)",
                  }}
                  title={t('cam_mfg_verified_title', {
                    defaultValue: 'The manufacturer reviewed these specifications through the CCTVplanner verification program; CCTVplanner checked the submission editorially before publishing.',
                  })}
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {t('cam_mfg_verified_badge', {
                    defaultValue: 'Confirmed by {{brand}}',
                    brand: mfgVerified.brand,
                  })}
                </span>
              )}
            </div>
            <div
              className="border"
              style={{
                borderColor: "var(--color-gray-border)",
                backgroundColor: "var(--color-surface)",
                // Brand-blue rule ties the table to the eyebrow above it.
                borderLeft: "2px solid var(--color-brand-blue)",
              }}
            >
              {([
                [t('cam_spec_resolution', { defaultValue: 'Resolution' }), formatResolution(camera.resolution)],
                [t('cam_spec_focal_length', { defaultValue: 'Focal Length' }), camera.focalLength || t('cam_spec_na', { defaultValue: 'N/A' })],
                [t('cam_spec_horizontal_fov', { defaultValue: 'Horizontal FOV' }), camera.figureSource === 'estimated' ? t('cam_spec_fov_estimated', { defaultValue: '{{hfov}}° (estimated from the lens)', hfov: camera.hfov }) : `${camera.hfov}°`],
                [t('cam_spec_effective_range', { defaultValue: 'Effective Range' }), effRange > 0 ? `${effRange}m` : t('cam_spec_na', { defaultValue: 'N/A' })],
                [t('cam_spec_ir_range', { defaultValue: 'IR Range' }), `${camera.irRange}m`],
                [t('cam_spec_type', { defaultValue: 'Type' }), t(`cam_type_${camera.type}`, { defaultValue: getCameraTypeName(camera.type) })],
                [t('cam_spec_low_light', { defaultValue: 'Low Light' }), camera.superLowLight ? (typeof lowLightLux === 'number' ? t('cam_spec_low_light_yes_lux', { defaultValue: 'Yes — Super Low Light ({{lux}} lux)', lux: lowLightLux }) : t('cam_spec_low_light_yes', { defaultValue: 'Yes — Super Low Light' })) : t('cam_spec_low_light_standard', { defaultValue: 'Standard' })],
                // Datasheet-verified rows (only when an audited spec exists) —
                // surfaces fields that GSC shows ranking but the page never rendered.
                ...(vspec ? ([
                  vspec.sensorSize ? [t('cam_spec_sensor_size', { defaultValue: 'Sensor Size' }), vspec.sensorSize] : null,
                  typeof vspec.minIlluminationColorLux === 'number'
                    ? [t('cam_spec_low_light_color', { defaultValue: 'Low Light (colour)' }), `${vspec.minIlluminationColorLux} lux`]
                    : (typeof vspec.minIllumination_lux === 'number' ? [t('cam_spec_min_illumination', { defaultValue: 'Min. Illumination' }), `${vspec.minIllumination_lux} lux`] : null),
                  typeof vspec.minIlluminationBwLux === 'number' ? [t('cam_spec_low_light_bw', { defaultValue: 'Low Light (B/W)' }), `${vspec.minIlluminationBwLux} lux`] : null,
                  vspec.ipRating ? [t('cam_spec_ip_rating', { defaultValue: 'Ingress Protection (IP)' }), vspec.ipRating] : null,
                  vspec.ikRating ? [t('cam_spec_ik_rating', { defaultValue: 'Impact Rating (IK)' }), vspec.ikRating] : null,
                  typeof vspec.powerConsumption_w === 'number' ? [t('cam_spec_power', { defaultValue: 'Power Consumption' }), `${vspec.powerConsumption_w} W`] : null,
                  typeof vspec.maxBitrate_mbps === 'number' ? [t('cam_spec_max_bitrate', { defaultValue: 'Max Bitrate' }), `${vspec.maxBitrate_mbps} Mbps`] : null,
                  vspec.codecsSupported?.length ? [t('cam_spec_codecs', { defaultValue: 'Codecs' }), vspec.codecsSupported.join(', ')] : null,
                  vspec.onvifProfiles?.length ? [t('cam_spec_onvif', { defaultValue: 'ONVIF Profiles' }), vspec.onvifProfiles.map((p) => `Profile ${p}`).join(', ')] : null,
                  (typeof vspec.operatingTemp_min_c === 'number' && typeof vspec.operatingTemp_max_c === 'number')
                    ? [t('cam_spec_operating_temp', { defaultValue: 'Operating Temp.' }), t('cam_spec_temp_range', { min: vspec.operatingTemp_min_c, max: vspec.operatingTemp_max_c, defaultValue: '{{min}}°C to {{max}}°C' })]
                    : null,
                  typeof vspec.ndaaCompliant === 'boolean' ? [t('cam_spec_ndaa', { defaultValue: 'NDAA 889 Compliant' }), vspec.ndaaCompliant ? t('cam_spec_yes', { defaultValue: 'Yes' }) : t('cam_spec_no', { defaultValue: 'No' })] : null,
                ].filter(Boolean) as [string, string][]) : []),
              ] as [string, string][]).map(([label, value], i) => (
                <div
                  key={label}
                  className="flex justify-between items-baseline px-6 py-4"
                  style={{ borderTop: i !== 0 ? "1px solid var(--color-gray-border)" : undefined }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-mono), monospace",
                      fontSize: "10px",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--color-gray-muted)",
                    }}
                  >
                    {label}
                  </span>
                  <span className="text-foreground font-medium">{value}</span>
                </div>
              ))}
            </div>
            {camera.figureSource && (
              <p className="text-[12px] text-[var(--color-gray-muted)] mt-3 max-w-3xl">
                {camera.figureSource === 'estimated'
                  ? t('cam_figures_estimated', { defaultValue: "{{manufacturer}} does not state this camera's field of view on the pages we read, so the angle is estimated from its lens. The other figures are as catalogued.", manufacturer: camera.manufacturer })
                  : t('cam_figures_unconfirmed', { defaultValue: "These figures are from our earlier catalogue and have not yet been confirmed against {{manufacturer}}'s own datasheet.", manufacturer: camera.manufacturer })}
              </p>
            )}
            {(vspec || makerUrl) && (
              <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                <p className="text-[11px] text-[var(--color-gray-muted)]/70">
                  {t('cam_spec_source', { defaultValue: 'Verified specifications sourced from the official manufacturer datasheet.' })}
                </p>
                {sourceUrl && (
                  <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-brand-blue-text)] hover:underline"
                  >
                    {t('cam_view_datasheet', { defaultValue: 'View official datasheet' })}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
            {mfgVerified && (
              <p className="text-[11px] text-[var(--color-gray-muted)]/70 mt-3 flex items-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-[1px]" style={{ color: "rgb(59,130,246)" }} />
                <span>
                  {t('cam_mfg_verified_note', {
                    defaultValue:
                      'The field of view, range and IR specifications on this page were reviewed and confirmed by {{brand}} in {{date}} through the CCTVplanner manufacturer verification program, then editorially checked by CCTVplanner before publication.',
                    brand: mfgVerified.brand,
                    date: mfgVerifiedDate,
                  })}
                </span>
              </p>
            )}

            {/* Manufacturer acquisition CTA — the person most likely to read
                this table line by line is the manufacturer's own product
                team. Routes into the open /verify flow (brand access request,
                founder-approved). Hidden once the model already carries the
                brand-confirmed badge. */}
            {!mfgVerified && (
              <div
                className="mt-6 p-5 flex flex-wrap items-center gap-4 justify-between border"
                style={{
                  borderColor: "var(--color-gray-border)",
                  backgroundColor: "var(--color-surface)",
                  borderLeft: "2px solid var(--color-brand-blue)",
                }}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <ShieldCheck className="w-6 h-6 shrink-0" style={{ color: "var(--color-brand-blue-text)" }} />
                  <div>
                    <p className="font-semibold text-foreground m-0">
                      {t('cam_mfg_cta_title', {
                        defaultValue: 'Are you the manufacturer of this camera?',
                      })}
                    </p>
                    <p className="text-sm text-[var(--color-gray-muted)] m-0 mt-1 max-w-xl">
                      {t('cam_mfg_cta_body', {
                        defaultValue:
                          'Verify the specifications of your {{brand}} models and earn the "Confirmed by the manufacturer" badge — shown on these pages and next to your cameras inside the planner installers design with. Free of charge.',
                        brand: camera.manufacturer,
                      })}
                    </p>
                  </div>
                </div>
                <Link
                  to="/verify"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold shrink-0"
                  style={{ backgroundColor: "var(--color-brand-blue)", color: "#fff" }}
                >
                  {t('cam_mfg_cta_button', { defaultValue: 'Verify your specs' })}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </section>

          {/* Unique narrative — overview paragraph (EN-only until templates are translated) */}
          {isEnglish && (
            <section className="mb-12">
              <h2 className="bd-h2 mb-6">
                About the {camera.manufacturer} {camera.model}
              </h2>
              <p className="text-base leading-relaxed text-[var(--color-gray-muted)]">
                {introParagraph}
              </p>
            </section>
          )}

          {/* Best use cases — varied per camera spec (EN-only) */}
          {isEnglish && useCases.length > 0 && (
            <section className="mb-12">
              <h2 className="bd-h2 mb-8">
                Best use cases for this camera
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {useCases.map((uc) => (
                  <div
                    key={uc.title}
                    className="bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] rounded-none p-5"
                  >
                    <h3 className="bd-h3 mb-2 flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-[var(--color-brand-blue-text)] mt-0.5 flex-shrink-0" />
                      {uc.title}
                    </h3>
                    <p className="text-sm text-[var(--color-gray-muted)] leading-relaxed">
                      {uc.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Strengths & considerations — pros/cons varied per spec (EN-only) */}
          {isEnglish && (
            <section className="mb-12 grid md:grid-cols-2 gap-6">
              <div className="bg-[var(--color-surface)]/60 border border-emerald-500/30 dark:border-emerald-900/40 rounded-none p-6">
                <h3 className="bd-h3 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Strengths
                </h3>
                <ul className="space-y-2">
                  {strengths.map((s, i) => (
                    <li key={i} className="text-sm text-[var(--color-gray-muted)] flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">•</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[var(--color-surface)]/60 border border-amber-500/30 dark:border-amber-900/40 rounded-none p-6">
                <h3 className="bd-h3 mb-4 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-amber-400" />
                  Considerations
                </h3>
                <ul className="space-y-2">
                  {considerations.map((c, i) => (
                    <li key={i} className="text-sm text-[var(--color-gray-muted)] flex items-start gap-2">
                      <span className="text-amber-400 mt-1">•</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Coverage Table */}
          <section className="mb-16">
            <h2 className="bd-h2 mb-8">
              {t('cam_coverage_title', { defaultValue: 'Coverage at Different Distances' })}
            </h2>
            <div className="bg-[var(--color-surface)] border border-[var(--color-gray-border)] rounded-none overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[var(--color-surface)]/80">
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-gray-muted)]">{t('cam_coverage_distance', { defaultValue: 'Distance' })}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-gray-muted)]">{t('cam_coverage_width', { defaultValue: 'Coverage Width' })}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-gray-muted)]">{t('cam_coverage_area', { defaultValue: 'Coverage Area' })}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-gray-muted)]">{t('cam_coverage_use_case', { defaultValue: 'Use Case' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {distances.map((d, i) => {
                    const width = calculateCoverage(camera.hfov, d);
                    const area = width * d;
                    const useCase =
                      d <= 5 ? t('cam_usecase_entrance', { defaultValue: 'Entrance, corridor' }) :
                      d <= 10 ? t('cam_usecase_room', { defaultValue: 'Room, retail aisle' }) :
                      d <= 15 ? t('cam_usecase_warehouse', { defaultValue: 'Warehouse aisle, loading bay' }) :
                      d <= 20 ? t('cam_usecase_parking_row', { defaultValue: 'Parking row, courtyard' }) :
                      d <= 30 ? t('cam_usecase_parking', { defaultValue: 'Parking lot, yard' }) :
                      t('cam_usecase_perimeter', { defaultValue: 'Perimeter, large area' });
                    return (
                      <tr key={d} className={i % 2 === 0 ? "bg-[var(--color-surface)]/40" : ""}>
                        <td className="px-4 py-3 text-foreground font-medium">{d}m</td>
                        <td className="px-4 py-3 text-foreground">{width.toFixed(1)}m</td>
                        <td className="px-4 py-3 text-foreground">{area.toFixed(0)} m²</td>
                        <td className="px-4 py-3 text-[var(--color-gray-muted)] text-sm">{useCase}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* FOV Visualization — DORI tier bands from the designer's engine,
              one scene scale that fits every radius (the old sketch let a
              40 m IR arc fly off a 12 m-scaled canvas). */}
          <section className="mb-16">
            <h2 className="bd-h2 mb-8">{t('cam_fov_viz', { defaultValue: 'Field of View Visualization' })}</h2>
            <FovVisualization
              hfov={camera.hfov}
              effRange={effRange}
              irRange={camera.irRange}
              resolution={camera.resolution}
            />
            {/* Product hook exactly where interest peaks: the reader is
                looking at this camera's coverage — one click puts the same
                cone on THEIR site plan. Quiet text link, not a third button. */}
            <p className="mt-3 text-sm">
              <button
                onClick={handleCTA}
                className="inline-flex items-center gap-1.5 font-medium text-[var(--color-brand-blue-text)] hover:underline bg-transparent border-0 p-0 cursor-pointer"
              >
                {t('cam_viz_cta', {
                  defaultValue: 'See these zones on your own site plan — drop the {{model}} onto a map',
                  model: camera.model,
                })}
                <ArrowRight className="w-4 h-4" />
              </button>
            </p>
          </section>

          {/* Night Vision Section */}
          <section className="mb-16">
            <h2 className="bd-h2 mb-8">
              {t('cam_night_vision_title', { defaultValue: 'Night Vision Performance' })}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[var(--color-surface)] border border-[var(--color-gray-border)] rounded-none p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Sun className="w-5 h-5 text-yellow-400" />
                  <h3 className="font-semibold">{t('cam_daytime', { defaultValue: 'Daytime' })}</h3>
                </div>
                <p className="text-[var(--color-gray-muted)] text-sm">
                  {t('cam_daytime_desc', {
                    defaultValue: 'Full {{resolution}} resolution at {{hfov}}° field of view. Effective identification range up to {{range}}m in good lighting conditions.',
                    resolution: formatResolution(camera.resolution),
                    hfov: camera.hfov,
                    range: getDORIDistanceM(camera, 'identify') || effRange,
                  })}
                </p>
              </div>
              <div className="bg-[var(--color-surface)] border border-[var(--color-gray-border)] rounded-none p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Moon className="w-5 h-5 text-blue-400" />
                  <h3 className="font-semibold">{t('cam_nighttime', { defaultValue: 'Nighttime (IR)' })}</h3>
                </div>
                <p className="text-[var(--color-gray-muted)] text-sm">
                  {t('cam_nighttime_desc', {
                    defaultValue: 'Built-in IR LEDs illuminate up to {{irRange}}m in complete darkness.',
                    irRange: camera.irRange,
                  })}
                  {camera.superLowLight
                    ? ` ${typeof lowLightLux === 'number'
                        ? t('cam_nighttime_super_low_light_lux', { defaultValue: 'Super Low Light sensor captures colour images in near-darkness (down to {{lux}} lux).', lux: lowLightLux })
                        : t('cam_nighttime_super_low_light_nolux', { defaultValue: 'Super Low Light sensor captures colour images in near-darkness.' })}`
                    : ` ${t('cam_nighttime_bw', { defaultValue: 'Switches to B/W mode for clear night footage.' })}`}
                </p>
              </div>
            </div>
          </section>

          {/* Installation tips — varied per camera type/FOV/range (EN-only) */}
          {isEnglish && installationTips.length > 0 && (
            <section className="mb-16">
              <h2 className="bd-h2 mb-8">
                Installation tips for the {camera.model}
              </h2>
              <div className="space-y-3">
                {installationTips.map((tip, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] rounded-none p-4"
                  >
                    <div className="w-7 h-7 rounded-full bg-[rgba(56,112,230,0.15)] text-[var(--color-brand-blue-text)] flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <p className="text-sm text-[var(--color-gray-muted)] leading-relaxed pt-0.5">
                      {tip}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* DORI distances grid — numbers-only, language-neutral. EN paragraph
              hidden under non-EN UI (see narrative templates EN-only note above). */}
          <section className="mb-16">
            <h2 className="bd-h2 mb-6">
              {t('cam_coverage_math', { defaultValue: 'Coverage geometry & DORI distances' })}
            </h2>
            {isEnglish && (
              <p className="text-sm text-[var(--color-gray-muted)] leading-relaxed">
                {coverageMath.paragraph}
              </p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
              {([
                { label: t('cam_dori_detect', { defaultValue: 'Detect' }), value: coverageMath.doriDistances.detect, color: 'text-green-400' },
                { label: t('cam_dori_observe', { defaultValue: 'Observe' }), value: coverageMath.doriDistances.observe, color: 'text-yellow-400' },
                { label: t('cam_dori_recognize', { defaultValue: 'Recognise' }), value: coverageMath.doriDistances.recognize, color: 'text-orange-400' },
                { label: t('cam_dori_identify', { defaultValue: 'Identify' }), value: coverageMath.doriDistances.identify, color: 'text-red-400' },
              ] as const).map((item) => (
                <div key={item.label} className="bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] p-3">
                  <div className="text-[11px] uppercase tracking-wide text-[var(--color-gray-muted)]">{item.label}</div>
                  <div className={`text-2xl font-mono font-semibold ${item.color}`}>{item.value} m</div>
                </div>
              ))}
            </div>
          </section>

          {/* Power & cabling, Privacy/GDPR, Installer cost, Extended FAQ —
              all rendered from EN narrative templates. Hidden under non-EN UI. */}
          {isEnglish && (
            <>
              <section className="mb-16">
                <h2 className="bd-h2 mb-6">
                  Power & cabling
                </h2>
                <p className="text-sm text-[var(--color-gray-muted)] leading-relaxed">
                  {powerCabling.paragraph}
                </p>
              </section>

              <section className="mb-16">
                <h2 className="bd-h2 mb-6">
                  Privacy & legal (GDPR / RODO)
                </h2>
                <p className="text-sm text-[var(--color-gray-muted)] leading-relaxed">
                  {privacyNote}
                </p>
              </section>

              <section className="mb-16">
                <h2 className="bd-h2 mb-6">
                  Installer time & cost (rough estimate)
                </h2>
                <p className="text-sm text-[var(--color-gray-muted)] leading-relaxed">
                  {installerCostNote}
                </p>
                <p className="text-[11px] text-[var(--color-gray-muted)]/70 mt-2">
                  Indicative EU 2024-2025 pricing — actual quotes vary by region, mounting height, and site conditions. Excludes hardware, transport, permits, and VAT.
                </p>
              </section>

              <section className="mb-16">
                <h2 className="bd-h2 mb-8">
                  Frequently asked questions
                </h2>
                <div className="space-y-4">
                  {extendedFaq.map((qa, i) => (
                    <div key={i} className="border-l-2 border-[var(--color-brand-blue)] pl-4">
                      <h3 className="text-sm font-semibold mb-1">{qa.question}</h3>
                      <p className="text-sm text-[var(--color-gray-muted)] leading-relaxed">{qa.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

          {/* Related Cameras */}
          {related.length > 0 && (
            <section className="mb-16">
              <h2 className="bd-h2 mb-8">
                {t('cam_related_cameras', { defaultValue: 'Related {{manufacturer}} {{type}}s', manufacturer: camera.manufacturer, type: getCameraTypeName(camera.type) })}
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {related.map(c => (
                  <Link
                    key={c.slug}
                    to={c.path}
                    className="bg-[var(--color-surface)] border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group"
                  >
                    <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors">
                      {c.model}
                    </p>
                    <p className="text-sm text-[var(--color-gray-muted)] mt-1">
                      {formatResolution(c.resolution)} · {c.hfov}° · {c.irRange}m IR
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Cross-sell */}
          {crossSell.length > 0 && (
            <section className="mb-16">
              <h2 className="bd-h2 mb-8">
                {t('cam_other_cameras', { defaultValue: 'Other {{manufacturer}} cameras', manufacturer: camera.manufacturer })}
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {crossSell.map(c => (
                  <Link
                    key={c.slug}
                    to={c.path}
                    className="bg-[var(--color-surface)] border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group"
                  >
                    <p className="text-xs text-[var(--color-brand-blue-text)] mb-1">{t(`cam_type_${c.type}`, { defaultValue: getCameraTypeName(c.type) })}</p>
                    <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">
                      {c.model}
                    </p>
                    <p className="text-xs text-[var(--color-gray-muted)] mt-1">
                      {c.hfov}° · {getEffectiveRangeM(c)}m
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Side-by-side comparisons featuring this camera */}
          {comparisons.length > 0 && (
            <section className="mb-16">
              <h2 className="bd-h2 mb-8">
                {t('cam_compare_head', { defaultValue: 'Compare the {{model}}', model: camera.model })}
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {comparisons.map(c => {
                  const other = getCameraBySlug(c.otherSlug);
                  if (!other) return null;
                  return (
                    <Link
                      key={c.slug}
                      to={`/compare/sku/${c.slug}`}
                      className="bg-[var(--color-surface)] border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group"
                    >
                      <p className="text-xs text-[var(--color-brand-blue-text)] mb-1">
                        {t('cam_compare_kicker', { defaultValue: 'Side-by-side' })}
                      </p>
                      <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">
                        {t('cam_compare_pair', { defaultValue: '{{a}} vs {{b}}', a: camera.model, b: other.model })}
                      </p>
                      <p className="text-xs text-[var(--color-gray-muted)] mt-1">
                        {formatResolution(other.resolution)} · {other.hfov}° · {other.irRange}m IR
                      </p>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Helpful Tools & Resources */}
          <section className="mb-16">
            <h2 className="bd-h2 mb-8">{t('cam_helpful_tools', { defaultValue: 'Helpful Tools & Resources' })}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/cctv-design-tool" className="bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group">
                <PencilRuler className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">{t('cam_tool_design_title', { defaultValue: 'Free CCTV Design Tool' })}</p>
                <p className="text-xs text-[var(--color-gray-muted)] mt-1">{t('cam_tool_design_desc', { defaultValue: 'Plan a full camera layout online — no download, free to start' })}</p>
              </Link>
              <Link to="/camera-placement-tool" className="bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group">
                <MapPin className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">{t('cam_tool_placement_tool_title', { defaultValue: 'Camera Placement Tool' })}</p>
                <p className="text-xs text-[var(--color-gray-muted)] mt-1">{t('cam_tool_placement_tool_desc', { defaultValue: 'Position cameras on a real floor plan and see live DORI coverage' })}</p>
              </Link>
              <Link to="/calculator/fov" className="bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group">
                <Eye className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">{t('cam_tool_fov_title', { defaultValue: 'FOV Calculator' })}</p>
                <p className="text-xs text-[var(--color-gray-muted)] mt-1">{t('cam_tool_fov_desc', { defaultValue: 'Calculate field of view for any focal length' })}</p>
              </Link>
              <Link to="/calculator/storage" className="bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group">
                <Zap className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">{t('cam_tool_storage_title', { defaultValue: 'Storage Calculator' })}</p>
                <p className="text-xs text-[var(--color-gray-muted)] mt-1">{t('cam_tool_storage_desc', { defaultValue: 'Estimate HDD requirements for your setup' })}</p>
              </Link>
              <Link to="/calculator/how-many-cameras" className="bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group">
                <Camera className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">{t('cam_tool_quantity_title', { defaultValue: 'Camera Quantity Calculator' })}</p>
                <p className="text-xs text-[var(--color-gray-muted)] mt-1">{t('cam_tool_quantity_desc', { defaultValue: 'Find out how many cameras you need' })}</p>
              </Link>
              <Link to="/blog/field-of-view-explained" className="bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group">
                <Maximize2 className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">{t('cam_tool_fov_explained_title', { defaultValue: 'Field of View Explained' })}</p>
                <p className="text-xs text-[var(--color-gray-muted)] mt-1">{t('cam_tool_fov_explained_desc', { defaultValue: 'Understand HFOV, VFOV, and lens selection' })}</p>
              </Link>
              <Link to="/blog/camera-placement-guide" className="bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group">
                <Crosshair className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">{t('cam_tool_placement_title', { defaultValue: 'Camera Placement Guide' })}</p>
                <p className="text-xs text-[var(--color-gray-muted)] mt-1">{t('cam_tool_placement_desc', { defaultValue: 'Best practices for positioning cameras' })}</p>
              </Link>
              <Link to="/features" className="bg-[var(--color-surface)]/60 border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group">
                <Shield className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">{t('cam_tool_all_features_title', { defaultValue: 'All Features' })}</p>
                <p className="text-xs text-[var(--color-gray-muted)] mt-1">{t('cam_tool_all_features_desc', { defaultValue: 'See everything CCTVplanner can do' })}</p>
              </Link>
            </div>
          </section>

        </div>

        {/* DARK FINAL CTA */}
        <section
          className="bd-section bd-section--dark always-dark py-20 md:py-28"
          style={{ backgroundColor: "var(--color-gray-dark)" }}
        >
          <div className="pelco-container text-center max-w-3xl">
            <h2
              className="font-bold mb-6"
              style={{
                fontFamily: "var(--font-body), 'Instrument Sans', sans-serif",
                fontSize: "clamp(28px, 4vw, 40px)",
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
                color: "var(--color-page-bg)",
              }}
            >
              {t('cam_plan_cta_title', { defaultValue: 'Plan your layout with {{manufacturer}} {{model}}', manufacturer: camera.manufacturer, model: camera.model })}
            </h2>
            <p
              className="mb-8 max-w-2xl mx-auto"
              style={{
                fontFamily: "var(--font-body), sans-serif",
                fontSize: "16px",
                lineHeight: 1.6,
                color: "rgba(255, 255, 255, 0.7)",
              }}
            >
              {t('cam_plan_cta_desc', { defaultValue: 'Use our free CCTV planner to place this camera on your floor plan, see real-time field of view coverage, and generate a professional PDF report.' })}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={handleCTA}
                className="bd-btn bd-btn-primary"
                style={{
                  backgroundColor: "var(--color-page-bg)",
                  color: "var(--color-gray-dark)",
                  borderColor: "var(--color-page-bg)",
                }}
              >
                {t('cam_start_planning_free', { defaultValue: 'Start planning for free' })}
                <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/demo"
                className="text-sm font-medium hover:underline"
                style={{ color: "rgba(255, 255, 255, 0.75)" }}
              >
                {t('cam_demo_link', { defaultValue: 'or explore the live demo first, no account needed' })}
              </Link>
            </div>
            <p
              className="mt-8 tracking-wide"
              style={{
                fontFamily: "var(--font-mono), monospace",
                fontSize: "11px",
                color: "rgba(255, 255, 255, 0.55)",
              }}
            >
              {t('cam_installer_disclaimer_mono', { defaultValue: 'Free until you outgrow it · No card · No install' })}
            </p>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default CameraPage;
